import { Box, Typography } from '@mui/material';
import PassBuyFooter, { PASS_BUY_FOOTER_HEIGHT } from './PassBuyFooter';
import PassCardHeader from './PassCardHeader';
import { rapgemBoostSwap } from '../../assets/bazzar';

const CARD_WIDTH = 381;
const CARD_HEIGHT = 272;

/** Figma 11389:6183 — gem pass card shell */
const passCardOuterGradient = 'linear-gradient(to top, #822AFF 0%, #FF00F2 41.566%)';

const bodyTextSx = {
  color: '#fff',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
};

function PassGemIcon({ type }) {
  const isSwap = type === 'swap';

  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      <Box
        component="img"
        src={rapgemBoostSwap}
        alt=""
        aria-hidden
        sx={{
          width: '353.1%',
          height: '235.4%',
          maxWidth: 'none',
          display: 'block',
          position: 'absolute',
          left: isSwap ? '-242.53%' : '-127.36%',
          top: '-61.61%',
        }}
      />
    </Box>
  );
}

function PassBenefitRow({ type, label }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: type === 'swap' ? '10px' : '14px',
        minWidth: 0,
        width: '100%',
        pl: type === 'swap' ? '39px' : 0,
        position: 'relative',
      }}
    >
      {type === 'swap' ? (
        <Box sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
          <PassGemIcon type="swap" />
        </Box>
      ) : (
        <PassGemIcon type="boost" />
      )}
      <Typography sx={bodyTextSx}>{label}</Typography>
    </Box>
  );
}

/** Figma 11389:6181 — daily / weekly / monthly gem passes */
export default function GemPassCard({ card, onClick }) {
  const headerBackground = card.headerGradient || card.tone || '#2c0041';
  const headerColorText = card.toneText || '#ae00ff';
  const headerColorBorder = card.toneBorder || card.toneText || '#ae00ff';
  const buyLabel = card.actionLabel || card.priceLabel || card.price;

  return (
    <Box
      onClick={() => onClick(card)}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: CARD_WIDTH,
        height: 220,
        mx: 'auto',
        cursor: 'pointer',
        borderRadius: '22px',
        background: passCardOuterGradient,
        boxShadow: '0 8px 18px rgba(0, 0, 0, 0.4)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '2px',
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '80%',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            zIndex: 0,
          }}
        >
          <PassCardHeader background={headerBackground} sx={{ height: '100%' }} />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 190,
            maxWidth: 'calc(100% - 24px)',
            minWidth: 0,
            height: 55,
            bgcolor: 'rgba(0, 0, 0, 0.2)',
            border: `0.5px solid ${headerColorBorder}`,
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 1,
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              color: headerColorText,
              fontWeight: 600,
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.8px',
              textShadow: '0 1px 0 #fff',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {card.title}
          </Typography>
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 400,
              fontSize: 10,
              lineHeight: '12px',
              letterSpacing: 0,
              mt: 0.35,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {card.validity}
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: '80px',
            left: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '14px',
            width: 221,
            maxWidth: 'calc(100% - 32px)',
            zIndex: 1,
          }}
        >
          <PassBenefitRow type="swap" label={card.swapBenefit || 'Unlimited Swap'} />
          <PassBenefitRow type="boost" label={card.boostBenefit || 'Unlimited Boost'} />
        </Box>

        <PassBuyFooter
          onClick={(event) => {
            event.stopPropagation();
            onClick(card);
          }}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: `${(PASS_BUY_FOOTER_HEIGHT / CARD_HEIGHT) * 100}%`,
            minHeight: 'unset',
            zIndex: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 500,
                fontSize: 18,
                lineHeight: '22px',
                letterSpacing: 0,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {buyLabel}
            </Typography>
            {card.originalPrice ? (
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: 'normal',
                  letterSpacing: '0.8px',
                  opacity: 0.7,
                  textDecoration: 'line-through',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  mt: 0.25,
                }}
              >
                {card.originalPrice}
              </Typography>
            ) : null}
          </Box>
        </PassBuyFooter>
      </Box>
    </Box>
  );
}
